import * as React from 'react';

import {
  Button,
  DatePicker,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogTitle,
  type DateValue,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { LucideDownload } from 'lucide-react';

import { generateNachaFileAtom } from '../../_graphql/mutationGenerateNachaFile';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { SelectFinancialAccount } from '~/shared/components/select-fields/SelectFinancialAccount';
import { now } from '~/shared/utils/dateTime';

interface GenerateAchFileDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPaymentIds: string[];
  onSuccess?: () => void;
}

export function GenerateAchFileDialog({
  isOpen,
  onOpenChange,
  selectedPaymentIds,
  onSuccess,
}: GenerateAchFileDialogProps) {
  const t = useTranslations();
  const toast = useToast();
  const generateNachaFile = useSetAtom(generateNachaFileAtom());

  const [effectiveEntryDate, setEffectiveEntryDate] =
    React.useState<DateValue | null>(now());
  const [financialAccountId, setFinancialAccountId] = React.useState<
    string | null
  >(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleClose = () => {
    onOpenChange(false);
    setEffectiveEntryDate(now());
    setFinancialAccountId(null);
  };

  const handleSubmit = async () => {
    if (!financialAccountId || !effectiveEntryDate) return;

    try {
      setIsSubmitting(true);
      const result = await generateNachaFile({
        financialAccountId,
        paymentIds: selectedPaymentIds,
        effectiveEntryDate,
      });

      const fileContent = result?.financeGenerateNachaFile?.fileContent ?? '';
      const blob = new Blob([fileContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nacha_${new Date().toISOString().slice(0, 10)}.ach`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success(t.payments.achFile.messages.success);
      onSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(
        t.payments.achFile.messages.error(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      className="@GenerateAchFileDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle>{t.payments.achFile.title}</DialogTitle>
        </DialogHeader>
        <DialogMain>
          <div className="flex flex-col gap-4">
            <div className="text-sm text-neutral/60">
              {t.payments.achFile.description({
                count: selectedPaymentIds.length,
              })}
            </div>
            <SelectFinancialAccount
              label={t.payments.achFile.fields.financialAccount}
              value={financialAccountId}
              onChange={(value) => setFinancialAccountId(value ?? null)}
            />
            <DatePicker
              label={t.payments.achFile.fields.effectiveEntryDate}
              value={effectiveEntryDate}
              onChange={setEffectiveEntryDate}
            />
          </div>
        </DialogMain>
        <DialogFooter>
          <div className="flex justify-end gap-2">
            <Button variant="text" color="neutral" onPress={handleClose}>
              {t.payments.achFile.buttons.cancel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSubmit}
              isPending={isSubmitting}
              isDisabled={!financialAccountId || !effectiveEntryDate}
              icon={<LucideDownload className="size-4" />}
            >
              {t.payments.achFile.buttons.generate}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
}
